import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useIsFetching } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useToasts } from 'react-toast-notifications';
import ActionPanelWrapper from 'src/components/ui/ActionPanelWrapper';
import Alert from 'src/components/ui/Alert';
import { INVENTORY_ACTION_TYPES,INVENTORY_ACTIONS } from 'src/constants/inventory';
import { useLocation } from 'src/hooks/services/useLocation';
import { useLotsByUris } from 'src/hooks/services/useLot';
import { useMaterial } from 'src/hooks/services/useMaterials';
import {
  useCreateStockAction,
  useStockActionForRun,
  useUpdateStockAction,
} from 'src/hooks/services/useMaterialStockAction';
import { useStockActionRelationshipsForPiece } from 'src/hooks/services/useStockActions';
import useActionPanelStore from 'src/stores/useActionPanelStore';
import { isIndividualAssignment } from 'src/utils/inventoryManagementUtils';

import InventoryQuantitySummary from './InventoryQuantitySummary';
import LotContainersTable from './LotContainersTable';

const InventorySelectContainers = ({
  runUri,
  stock,
  initialLotUri,
  action,
  pieces,
  scannedContainerUri,
  pieceName,
  pieceGroupIndex,
}) => {
  const [containerAmounts, setContainerAmounts] = useState({});
  const [initialContainerAmounts, setInitialContainerAmounts] = useState({});
  const [expandedLots, setExpandedLots] = useState({});

  const { addToast } = useToasts();
  const { closeActionPanel, setActionPanelProps } = useActionPanelStore();

  const createStockAction = useCreateStockAction();
  const updateStockAction = useUpdateStockAction();

  const {
    data: material,
    isInitialLoading: materialLoading,
    error: materialError,
  } = useMaterial(stock?.material);

  const { data: location } = useLocation(stock?.location, 'location-by-stock');

  const {
    data: stockActions = [],
    isInitialLoading: stockActionLoading,
    error: stockActionError,
  } = useStockActionForRun(runUri, stock?.uri);

  const currentStockAction = useMemo(() => {
    if (!stockActions.length) return null;

    // Instruction Group case
    if (action === INVENTORY_ACTION_TYPES.GROUP) {
      return stockActions.find(
        action => action.metadata?.pieces?.length > 1
      ) ?? null;
    }

    // Individual Piece case
    if (action === INVENTORY_ACTION_TYPES.PIECE) {
      const currentPieceUri = pieces[0];
      if (!currentPieceUri) return null;

      return stockActions.find(
        action =>
          Array.isArray(action.metadata?.pieces) &&
          action.metadata.pieces.length === 1 &&
          action.metadata.pieces.includes(currentPieceUri)
      ) ?? null;
    }

    return null;
  }, [action, pieces, stockActions]);

  // For piece level, fetch relationships to get group inheritance
  const pieceUri = action === INVENTORY_ACTION_TYPES.PIECE && pieces.length === 1 ? pieces[0] : null;
  const {
    data: pieceRelationships = [],
  } = useStockActionRelationshipsForPiece(pieceUri, runUri);

  // Calculate group quantity for piece level
  const groupQuantity = useMemo(() => {
    if (action !== INVENTORY_ACTION_TYPES.PIECE || !pieceUri) return 0;

    const groupRelationships = pieceRelationships.filter(relationship => {
      const stockAction = stockActions.find(sa => sa.uri === relationship.stock_action);
      return stockAction &&
             stockAction.source_stock === stock.uri &&
             !isIndividualAssignment(stockAction, pieceUri);
    });

    return groupRelationships.reduce((sum, rel) => sum + (Math.abs(rel.quantity || 0)), 0);
  }, [action, pieceUri, pieceRelationships, stock?.uri, stockActions]);

  // Check if there are individual piece assignments for this stock
  // Only relevant for instruction group view
  const hasIndividualPieceAssignments = useMemo(() => {
    if (action !== INVENTORY_ACTION_TYPES.GROUP || !stockActions.length) {
      return false;
    }

    // Check if any stock actions have single piece assignments
    return stockActions.some(
      action =>
        Array.isArray(action.metadata?.pieces) &&
        action.metadata.pieces.length === 1
    );
  }, [action, stockActions]);

  const {
    data: materialLots = [],
    isInitialLoading: materialLotsLoading,
    error: materialLotsError,
  } = useLotsByUris(stock?.material_lots);

  // Sort lots to put scanned Initial Lot first
  const sortedLots = useMemo(() => {
    if (!materialLots.length || !initialLotUri) return materialLots;

    const initialLotIndex = materialLots.findIndex(lot => lot.uri === initialLotUri);
    if (initialLotIndex === -1) return materialLots;

    const initialLot = materialLots[initialLotIndex];
    const otherLots = materialLots.filter(lot => lot.uri !== initialLotUri);

    return [initialLot, ...otherLots];
  }, [materialLots, initialLotUri]);

  // Initialize expanded state: expand initial lot by default
  // Only run once when initialLotUri is set
  useEffect(() => {
    if (initialLotUri && !expandedLots[initialLotUri]) {
      setExpandedLots(prev => ({ ...prev, [initialLotUri]: true }));
    }
  }, [initialLotUri]);

  // Extract container URIs that have amounts set in the current stock action
  const containerUrisWithAmounts = useMemo(() => {
    if (!currentStockAction?.metadata?.material_lot_containers) {
      return [];
    }
    return currentStockAction.metadata.material_lot_containers.map(({ uri }) => uri);
  }, [currentStockAction]);

  // Initialize container amounts from existing stock actions
  useEffect(() => {
    if (currentStockAction?.metadata?.material_lot_containers) {
      const initialAmounts = {};
      currentStockAction.metadata.material_lot_containers.forEach(({ uri, quantity }) => {
        initialAmounts[uri] = { quantity, error: '' };
      });
      setContainerAmounts(initialAmounts);
      // Store initial amounts for validation (these don't change during editing)
      setInitialContainerAmounts(initialAmounts);
    }
  }, [currentStockAction]);

  const handleToggleLot = useCallback((lotUri) => {
    setExpandedLots(prev => ({
      ...prev,
      [lotUri]: !prev[lotUri],
    }));
  }, []);

  const handleAmountChange = useCallback((containerUri, quantity, error) => {
    setContainerAmounts(prev => ({
      ...prev,
      [containerUri]: { quantity, error },
    }));
  }, []);

  const hasValidationErrors = useMemo(() => {
    return Object.values(containerAmounts).some(data => data.error);
  }, [containerAmounts]);

  const hasAnyAmounts = useMemo(() => {
    return Object.values(containerAmounts).some(data => data.quantity > 0);
  }, [containerAmounts]);

  // Calculate current sum of all container amounts in real-time
  const currentContainerSum = useMemo(() => {
    return Object.values(containerAmounts)
      .reduce((sum, data) => sum + (data.quantity || 0), 0);
  }, [containerAmounts]);

  const handleSave = async () => {
    // Filter containers with amounts > 0 and no errors
    const materialLotContainers = Object.entries(containerAmounts)
      .filter(([, data]) => data.quantity > 0 && !data.error)
      .map(([uri, data]) => ({ uri, quantity: data.quantity }));

    if (materialLotContainers.length === 0) {
      addToast('Please enter at least one container amount', { appearance: 'warning' });
      return;
    }

    setActionPanelProps({ isSubmitting: true });

    try {
      const isNewAction = currentStockAction === null;

      if (isNewAction) {
        // POST - Create new stock action
        const payload = {
          /* eslint-disable camelcase */
          action_type: INVENTORY_ACTIONS.CONSUME,
          source_stock: stock.uri,
          run: runUri,
          metadata: {
            pieces: pieces,
            material_lot_containers: materialLotContainers,
          },
        };

        await createStockAction.mutateAsync(payload);
      } else {
        // PUT - Update existing stock action
        const payload = {
          action_type: INVENTORY_ACTIONS.CONSUME,
          metadata: {
            pieces: pieces,
            material_lot_containers: materialLotContainers,
          },
        };

        await updateStockAction.mutateAsync({
          stockActionUri: currentStockAction.uri,
          payload,
        });
      }
      // Show success message
      setActionPanelProps({
        isSubmitting: false,
        successMessage: isNewAction
          ? 'Material assignment created successfully'
          : 'Material assignment updated successfully',
      });
    } catch (error) {
      console.error('Error saving stock action:', error);
      setActionPanelProps({
        isSubmitting: false,
        errorMessage: error.message || 'Failed to save stock action. Please try again.',
      });
    }
  };

  // Aggregate loading and error states
  const isLoadingData = materialLotsLoading || materialLoading || stockActionLoading;
  const error = materialLotsError || materialError || stockActionError;

  // Check if any container queries are currently loading using React Query's built-in state
  const isLoadingContainers = useIsFetching({ queryKey: ['containers-paginated'] }) > 0;

  return (
    <ActionPanelWrapper isLoading={isLoadingData} error={error} onClose={closeActionPanel}>
      <div className="inventory-material-info">
        <p>
          <strong>Material:</strong> {material?.name || 'Not Rendered'}
        </p>
        <p>
          <strong>Location:</strong> {location?.name || 'Not Rendered'}
        </p>
      </div>

      {/* Show it only if we scanned the stock, not editing existing one */}
      {(scannedContainerUri && initialLotUri) && currentStockAction && (
        <Alert
          variant="warning"
          icon={faExclamationTriangle}
          title="Same stock scanned"
        >
          You scanned the same stock. Would you like to update it?
          <div>
            <button
              type="button"
              className="btn btn-link alert-link p-0"
              onClick={closeActionPanel}
            >
              Back
            </button>
          </div>
        </Alert>
      )}

      {hasIndividualPieceAssignments && (
        <Alert
          variant="warning"
          icon={faExclamationTriangle}
          title="Individual material assignments"
        >
          Some pieces have individual material assignments for the same stock.
        </Alert>
      )}

      <InventoryQuantitySummary
        action={action}
        currentSum={currentContainerSum}
        units={material?.units || 'N/A'}
        groupQuantity={groupQuantity}
        pieceName={pieceName}
        pieceGroupIndex={pieceGroupIndex}
        isLoadingContainers={isLoadingContainers}
      />

      <div className="mb-3">
        {sortedLots.map((lot) => (
          <LotContainersTable
            key={lot.uri}
            lot={lot}
            units={material?.units || 'N/A'}
            isExpanded={!!expandedLots[lot.uri]}
            isScannedLot={lot.uri === initialLotUri}
            scannedContainerUri={scannedContainerUri}
            containerAmounts={containerAmounts}
            initialContainerAmounts={initialContainerAmounts}
            containerUrisWithAmounts={containerUrisWithAmounts}
            onAmountChange={handleAmountChange}
            onToggle={() => handleToggleLot(lot.uri)}
          />
        ))}
      </div>

      <button
        type="button"
        className="btn btn-lg btn-primary action-btn btn-block mt60"
        disabled={hasValidationErrors || !hasAnyAmounts}
        onClick={handleSave}
      >
        Save
      </button>
      <button
        type="button"
        className="btn btn-lg btn-block"
        onClick={closeActionPanel}
      >
        Cancel
      </button>
    </ActionPanelWrapper>
  );
};

InventorySelectContainers.propTypes = {
  runUri: PropTypes.string.isRequired,
  stock: PropTypes.shape({
    uri: PropTypes.string.isRequired,
    material: PropTypes.string,
    location: PropTypes.string,
    material_lots: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  initialLotUri: PropTypes.string,
  scannedContainerUri: PropTypes.string,
  action: PropTypes.oneOf([INVENTORY_ACTION_TYPES.GROUP, INVENTORY_ACTION_TYPES.PIECE]).isRequired,
  pieces: PropTypes.arrayOf(PropTypes.string).isRequired,
  pieceName: PropTypes.string,
  pieceGroupIndex: PropTypes.number,
};

InventorySelectContainers.defaultProps = {
  initialLotUri: null,
  scannedContainerUri: null,
  pieceName: null,
  pieceGroupIndex: null,
};

export default InventorySelectContainers;
